import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  HiOutlineUserGroup, 
  HiOutlineUserAdd,
  HiOutlineCheck,
  HiOutlineX,
  HiOutlineChevronDown,
  HiOutlineInbox
} from "react-icons/hi";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { toast } from "react-toastify";

const ITEMS_PER_PAGE = 4;

const InvitationCard = ({ invitation, onAccept, onReject, index }) => {
  const [processing, setProcessing] = useState(false);

  const handleAccept = async () => {
    setProcessing(true);
    await onAccept(invitation._id);
    setProcessing(false);
  };

  const handleReject = async () => {
    setProcessing(true);
    await onReject(invitation._id);
    setProcessing(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ delay: index * 0.1 }}
      className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border-l-4 border-gray-400 shadow-md hover:shadow-lg transition"
    >
      <div className="flex items-start gap-4">
        <img
          src={invitation.image || "/assets/DefaultImage.jpg"}
          alt={invitation.fullName}
          className="w-14 h-14 rounded-full object-cover ring-2 ring-gray-300"
        />
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-gray-800 truncate">
            {invitation.fullName || "Not Available"}
          </h4>
          <p className="text-sm text-gray-600 truncate">{invitation.email || "Not Available"}</p>
          {invitation.department && (
            <p className="text-xs text-gray-500 mt-1">{invitation.department}</p>
          )}
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleReject}
              disabled={processing}
              className="flex-1 px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition disabled:opacity-50 flex items-center justify-center gap-1"
            >
              <HiOutlineX className="w-4 h-4" />
              Decline
            </button>
            <button
              onClick={handleAccept}
              disabled={processing}
              className="flex-1 px-3 py-1.5 bg-gray-700 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition disabled:opacity-50 flex items-center justify-center gap-1"
            >
              <HiOutlineCheck className="w-4 h-4" />
              Accept
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const InvitationsPanel = () => {
  const [invitations, setInvitations] = useState([]);
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = Cookies.get("userToken");

  useEffect(() => {
    const fetchInvitations = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/apis/user/requestPending`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setInvitations(res.data.data || []);
      } catch (err) {
        console.error("Failed to fetch pending invitations", err);
      } finally {
        setLoading(false);
      }
    };
    fetchInvitations();
  }, [token]);

  const handleSeeMore = () => setVisibleCount(v => v + ITEMS_PER_PAGE);

  const handleAccept = async (id) => {
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/apis/user/acceptRequest/${id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvitations(inv => inv.filter(i => i._id !== id));
      toast.success(res.data.message);
    } catch {
      toast.error("Something went wrong");
    }
  };

  const handleReject = async (id) => {
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/apis/user/rejectRequest/${id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvitations(inv => inv.filter(i => i._id !== id));
      toast.success(res.data.message);
    } catch {
      toast.error("Something went wrong");
    }
  };

  const visibleInvitations = invitations.slice(0, visibleCount);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full bg-gradient-to-br from-gray-50 via-white to-gray-50 rounded-3xl shadow-xl p-6 border border-gray-200"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg flex items-center justify-center border border-gray-200">
            <HiOutlineUserGroup className="w-4 h-4 text-gray-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-700">Network</h3>
        </div>
        <button
          onClick={() => navigate("/connections")}
          className="text-sm text-gray-600 hover:text-gray-800 font-medium flex items-center"
        >
          See all
          <HiOutlineChevronDown className="w-4 h-4 ml-1 rotate-270" />
        </button>
      </div>

      <div className="mb-4">
        <p className="text-sm text-gray-600">
          {loading ? (
            <span className="animate-pulse">Loading...</span>
          ) : (
            <>
              <span className="font-semibold text-gray-700">{invitations.length}</span> Invitations Pending
            </>
          )}
        </p>
      </div>

      <div className="space-y-3 max-h-96 overflow-y-auto pr-2 custom-scrollbar">
        {loading ? (
          [...Array(3)].map((_, i) => (
            <div key={i} className="bg-gray-100 rounded-xl p-4 animate-pulse">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gray-200 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))
        ) : visibleInvitations.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-3 border border-gray-200">
              <HiOutlineInbox className="w-8 h-8 text-gray-500" />
            </div>
            <p className="text-gray-500">No invitations right now</p>
          </div>
        ) : (
          <AnimatePresence>
            {visibleInvitations.map((inv, index) => (
              <InvitationCard
                key={inv._id}
                invitation={inv}
                onAccept={handleAccept}
                onReject={handleReject}
                index={index}
              />
            ))}
          </AnimatePresence>
        )}
      </div>

      {!loading && visibleCount < invitations.length && (
        <button
          onClick={handleSeeMore}
          className="mt-4 w-full text-sm text-gray-600 hover:text-gray-800 font-medium py-2 border-t border-gray-200"
        >
          See more ({invitations.length - visibleCount} remaining)
        </button>
      )}
    </motion.div>
  );
};

export default InvitationsPanel;